<template>

  <v-container fluid class="page page-terms-of-use">

    <v-row align="center">
      <v-col class="d-flex align-center justify-center">
        <div class="form-title">Disclaimer & Terms of Use</div>
      </v-col>
    </v-row>

    <v-row class="mt-5 mx-3 page-content terms-content">
      <v-col align="left" class="py-0">
        <div class="terms-header">RouteLeaf Terms and Conditions</div>

        <div class="section-header">
          1. Introduction
        </div>
        <div class="section-text">
          Welcome to Star House, LLC ("Company", "we", "our", "us"). These Terms and Conditions
          ("Terms") govern your access to and use of our software as a service platform ("RouteLeaf"
          or "Service"), which helps suppliers deliver products securely and safely to retailers using a
          paperless system. By accessing or using the Service, you agree to be bound by these Terms.
          If you do not agree to these Terms, you may not use the Service.
        </div>

        <div class="section-header">
          2. Subscription and Access
        </div>
        <div class="section-text">
          <p>
            2.1 <i>Subscription</i>: Access to the Service is provided on a subscription basis. By subscribing to
            the Service, you agree to pay the subscription fees and any other charges incurred in
            connection with your use of the Service.
          </p>
          <p>
            2.2 <i>Account Security</i>: You are responsible for maintaining the confidentiality of your login
            credentials and for all activities that occur under your account. You agree to notify us
            immediately of any unauthorized use of your account or any other breach of security.
          </p>
          <p>
            2.3 <i>Updates</i>: We may update these Terms from time to time without prior notice. The
            updated Terms will be posted on the Service and will become effective immediately upon
            posting. Your continued use of the Service after the posting of the updated Terms
            constitutes your acceptance of the updated Terms.
          </p>
        </div>

        <div class="section-header">
          3. Use of the Service
        </div>
        <div class="section-text">
          <p>
            3.1 <i>License</i>: We grant you a limited, non-exclusive, non-transferable, and revocable license to access
            and use the Service in accordance with these Terms.
          </p>
          <p>
            3.2 <i>Prohibited Actions</i>: You agree not to:
            <ul style="margin-top:14px;">
              <li>Reverse engineer, decompile, or disassemble the Service or any part thereof.</li>
              <li>Use the Service for any unlawful purpose or in any manner that could damage, disable, overburden, or impair the Service.</li>
              <li>Use any automated means or form of data scraping or extraction to access, query, or extract data from the Service.</li>
            </ul>
          </p>
          <p>
            3.3 <i>User Data</i>: You are responsible for the accuracy and completeness of the data you input into the Service.
            We are not liable for any errors or inaccuracies in the data you provide.
          </p>
        </div>

        <div class="section-header">
          4. User Responsibilities and Compliance
        </div>
        <div class="section-text">
          <p>
            4.1 <i>Licensing and Compliance</i>: You represent and warrant that you hold all necessary
            licenses and permits required by applicable state and local laws to conduct your business in
            connection with the Service. You agree to comply with all applicable laws, regulations, and
            industry standards, particularly those relating to the sale and distribution of cannabis products.
          </p>
          <p>
            4.2 <i>Changes in Legal Status</i>: You agree to notify RouteLeaf immediately if there are any
            changes in your legal status, including but not limited to the suspension, revocation, or
            expiration of any licenses or permits.
          </p>
          <p>
            4.3 <i>Verification of Licensing</i>: RouteLeaf reserves the right to verify the status of ownership
            or management rights of any licenses represented on user profiles. We may revoke,
            transfer, or terminate any user profiles that fail this verification.
          </p>
        </div>

        <div class="section-header">
          5. Disclaimers
        </div>
        <div class="section-text">
          <p>
            5.1 <i>Not a Marijuana Company</i>: RouteLeaf is not a marijuana company and does not cultivate, manufacture,
            distribute, or sell marijuana or marijuana products. The Service provided by RouteLeaf is strictly for managing
            and facilitating the distribution process for licensed marijuana businesses.
          </p>
          <p>
            5.2 <i>No Marijuana Licenses</i>: RouteLeaf does not hold any marijuana licenses, nor does it provide legal
            advice or representation concerning marijuana-related business activities. It is the sole responsibility of the
            users to ensure they have the proper licenses and comply with all applicable laws.
          </p>
          <p>5.3 <i>No Responsibility for User Activities</i>: RouteLeaf is not responsible for the business activities of
            its users, including but not limited to the cultivation, manufacturing, distribution, sale, or transfer of
            marijuana or marijuana products. Users assume full responsibility for their actions and any legal consequences
            that may arise from their business activities.
          </p>
        </div>

        <div class="section-header">
          6. Intellectual Property
        </div>
        <div class="section-text">
          <p>
            6.1 <i>Ownership</i>: All intellectual property rights in and to the Service, including but not limited to
            software, design, text, graphics, and interfaces, are owned by us or our licensors. Nothing in these Terms
            grants you any rights in or to the Service other than the limited license to use the Service as expressly
            provided herein.
          </p>
          <p>
            6.2 <i>Protection</i>: You agree not to use, copy, modify, create derivative works of, or distribute any part
            of the Service except as expressly permitted by these Terms.
          </p>
        </div>

        <div class="section-header">
          7. Data Privacy and Security
        </div>
        <div class="section-text">
          <p>
            7.1 <i>Privacy Policy</i>: We are committed to protecting your privacy and will abide by all applicable
            data privacy laws, including but not limited to the Arizona data privacy laws and federal laws such as
            the Health Insurance Portability and Accountability Act (HIPAA) and the California Consumer Privacy Act (CCPA),
            where applicable. Our collection, use, and disclosure of personal information in connection with the Service
            are governed by our Privacy Policy. We collect personal information such as your name, email address, and
            payment details to provide and improve our Service. We do not sell your personal information to third parties.
            By using the Service, you consent to our collection, use, and disclosure of your personal information as
            described in the Privacy Policy.
          </p>
          <p>
            7.2 <i>Compliance with Arizona and Federal Laws</i>: We comply with all applicable data protection and
            privacy laws in the state of Arizona and federally in the United States. This includes taking all necessary
            steps to ensure that the data we collect, store, and process is handled in accordance with these laws. Users
            are also responsible for ensuring their compliance with relevant data privacy regulations in their jurisdictions.
          </p>
          <p>
            7.3 <i>Data Security</i>: We implement reasonable security measures to protect your data, in compliance with
            industry standards and applicable laws, including the Arizona data privacy laws and relevant federal regulations.
            However, you acknowledge that no method of transmission over the internet or electronic storage is completely
            secure, and we cannot guarantee the absolute security of your data.
          </p>
          <p>
            7.4 <i>Data Breach Notification</i>: In the event of a data breach that affects your personal information, we
              will notify you in accordance with applicable state and federal laws, including the Arizona Data Breach
              Notification Law and other relevant federal regulations. We will take all necessary steps to mitigate the
              breach and protect your data from further unauthorized access.
            </p>
          <p>
            7.5 <i>Third-Party Services</i>: We may share your data with third-party service providers to assist us in
            providing and improving the Service, but only in compliance with applicable laws and under agreements that
            require these providers to protect your data in accordance with similar standards of care as required by law.
          </p>
        </div>

        <div class="section-header">
          8. Limitation of Liability
        </div>
        <div class="section-text">
          <p>
            8.1 <i>Disclaimer of Warranties</i>: The Service is provided &quot;as is&quot; and &quot;as available&quot; without
            any warranties of any kind, either express or implied. We do not warrant that the Service will be uninterrupted,
            error-free, or secure.
          </p>
          <p>
            8.2 <i>Limitation of Liability</i>: To the fullest extent permitted by law, we shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred
            directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from (a) your use
            or inability to use the Service; (b) any unauthorized access to or use of our servers and/or any personal information
            stored therein; (c) any interruption or cessation of transmission to or from the Service; or (d) any bugs, viruses,
            trojan horses, or the like that may be transmitted to or through the Service by any third party.
          </p>
        </div>

        <div class="section-header">
          9. Termination
        </div>
        <div class="section-text">
          <p>
            We may terminate or suspend your access to the Service at any time, with or without cause, and with or without notice,
            effective immediately. Upon termination, your right to use the Service will immediately cease.
          </p>
        </div>

        <div class="section-header">
          10. Governing Law
        </div>
        <div class="section-text">
          <p>
            These Terms shall be governed and construed in accordance with the laws of the state of Arizona, without regard to
            its conflict of law provisions.
          </p>
        </div>

        <div class="section-header">
          11. Contact Us
        </div>
        <div class="section-text">
          <p>
            If you have any questions about these Terms, please contact us at <b>Contact@RouteLeaf.com</b>.
          </p>
        </div>

        <div class="section-header">
          12. Third-Party Integrations
        </div>
        <div class="section-text">
          <p>
            RouteLeaf may integrate with third-party services or platforms to enhance the functionality of our Service.
            We are not responsible for any actions, omissions, or policies of these third-party providers. Your use of
            third-party services is governed by their respective terms and conditions, and we do not assume liability for
            any issues that may arise from their use.
          </p>
        </div>

        <div class="section-header">
          13. Force Majeure
        </div>
        <div class="section-text">
            <p>
              RouteLeaf shall not be liable for any failure or delay in performing its obligations under these Terms due
              to circumstances beyond its reasonable control, including but not limited to acts of God, natural disasters,
              government actions, or interruptions in telecommunications or internet services.
            </p>
        </div>

        <div class="section-header">
          14. Data Retention Policy
        </div>
        <div class="section-text">
          <p>
            RouteLeaf will retain your personal information for as long as necessary to fulfill the purposes outlined in
            our Privacy Policy, unless a longer retention period is required or permitted by law. Upon request, and in
            accordance with applicable laws, we will delete your personal information when it is no longer needed for these
            purposes.
          </p>
        </div>

        <div class="section-header">
          15. Governing Law and Dispute Resolution
        </div>
        <div class="section-text">
          <p>
            These Terms shall be governed and construed in accordance with the laws of the state of Arizona, without regard
            to its conflict of law provisions. Any disputes arising out of or related to these Terms shall be resolved through
            binding arbitration in accordance with the rules of the American Arbitration Association, rather than in court,
            except where prohibited by law. The arbitration will take place in Arizona, and the decision of the arbitrator will
            be final and binding on both parties.
          </p>
        </div>

        <div class="section-header">
          16. User-Generated Content and Conduct
        </div>
        <div class="section-text">
          <p>
            Users are responsible for the content they submit or post on the Service. RouteLeaf reserves the right to remove or
            modify any content that violates these Terms, is unlawful, offensive, or otherwise inappropriate. By submitting content,
            you grant RouteLeaf a non-exclusive, royalty-free, perpetual license to use, reproduce, modify, and distribute your
            content in connection with the Service.
          </p>
        </div>

        <div class="section-header">
          17. Compliance with Cannabis Laws
        </div>
        <div class="section-text">
          <p>
            Users are solely responsible for ensuring compliance with all applicable federal, state, and local laws, including
            cannabis-related regulations. RouteLeaf does not provide legal advice, and use of the Service does not create an
            attorney-client relationship. We recommend consulting with a legal professional to ensure compliance with all relevant
            laws.
          </p>
        </div>

        <div class="section-header">
          18. Termination for Cause
        </div>
        <div class="section-text">
          <p>
            RouteLeaf reserves the right to terminate or suspend your access to the Service immediately and without notice if
            you violate these Terms, engage in fraudulent or illegal activities, or fail to comply with applicable laws, including
            cannabis regulations.
          </p>
        </div>

        <div class="section-header">
          19. Indemnification Specific to Cannabis Regulations
        </div>
        <div class="section-text">
          <p>
            You agree to indemnify, defend, and hold harmless RouteLeaf, its officers, directors, employees, and agents from
            and against any claims, liabilities, damages, losses, and expenses, including reasonable legal fees, arising out of
            or related to your use of the Service, your violation of these Terms, or any legal issues related to your
            cannabis-related activities.
          </p>
        </div>
      </v-col>
    </v-row>

    <v-row class="mt-7" v-if="showBackToApp">
      <v-col align="center">
        <v-btn
            color="primary"
            class="px-5"
            @click="$root.goToLastPage">
          Back to App
        </v-btn>
      </v-col>
    </v-row>

  </v-container>

</template>

<script>
export default {
  name: 'TermsOfUsePage',

  props: {
    showBackToApp: {
      type: Boolean,
      default: true
    }
  }
}
</script>

<style scoped>
  .page-terms-of-use {
    margin-top: 80px;
    max-width: 840px;
  }

  .page-terms-of-use .terms-content {
    border: 1px solid #3c3c3c;
    max-height: 640px;
    overflow-y: scroll;
  }

  .page-terms-of-use .form-title {
    font-size: 1.5rem;
    color: #F8A824;
    font-weight: 400;
  }

  .page-terms-of-use .terms-header {
    color: #dcdcdc;
    font-size: 1.3rem;
    font-weight: 600;
  }

  .page-terms-of-use .section-header {
    color: #dcdcdc;
    font-size: 1.1rem;
    font-weight: 600;
    margin: 20px 0;
  }

  .page-terms-of-use .section-text {
    color: #dcdcdc;
    font-size: 1.08rem;
    font-weight: 400;
  }
</style>
